import { supabase } from './supabase';
import { Resource } from './resources';

export interface BookingCommission {
  id: string;
  business_id: string;
  booking_id: string;
  resource_id: string;
  commission_percentage: number;
  sales_amount: number;
  commission_amount: number;
  status: 'pending' | 'paid' | 'voided';
  calculated_at: string;
  paid_at?: string;
  created_at: string;
  updated_at: string;
}

export interface BookingCommissionWithResource extends BookingCommission {
  resource: {
    id: string;
    first_name: string;
    last_name: string;
    email?: string;
  };
}

/**
 * Calculate commission amount based on sales amount and commission percentage
 */
export function calculateCommission(salesAmount: number, commissionPercentage: number): number {
  return (salesAmount * commissionPercentage) / 100;
}

/**
 * Get booking sales amount before discounts and taxes
 */
export function getBookingSalesBeforeDiscountsAndTax(booking: any): number {
  // Use subtotal which is the sum of all services before discounts and taxes
  return booking.subtotal || 0;
}

/**
 * Check if a resource is commission-based
 */
export function isCommissionResource(resource: Resource): boolean {
  return resource.compensation_type === 'commission' && (resource.commission_rate || 0) > 0;
}

/**
 * Get all commission-based resources from a booking
 */
export function getCommissionResourcesFromBooking(booking: any): Resource[] {
  if (!booking.resources) return [];
  
  return booking.resources
    .map((br: any) => br.resource)
    .filter((resource: Resource) => resource && isCommissionResource(resource));
}

/**
 * Create or update commission records for a booking
 */
export async function calculateAndSaveCommissions(
  bookingId: string,
  businessId: string,
  resources: Resource[],
  salesAmount: number
): Promise<BookingCommission[]> {
  if (!supabase) throw new Error('Supabase client not initialized');

  const commissions: BookingCommission[] = [];

  for (const resource of resources) {
    if (!isCommissionResource(resource)) continue;

    const commissionPercentage = resource.commission_rate || resource.commission_percentage || 0;
    const commissionAmount = calculateCommission(salesAmount, commissionPercentage);

    try {
      // Try to update existing commission first
      const { data: existingCommission, error: fetchError } = await supabase
        .from('booking_commissions')
        .select('*')
        .eq('booking_id', bookingId)
        .eq('resource_id', resource.id)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        // PGRST116 is "no rows returned", which is fine
        throw fetchError;
      }

      if (existingCommission) {
        // Update existing commission
        const { data: updatedCommission, error: updateError } = await supabase
          .from('booking_commissions')
          .update({
            commission_percentage: commissionPercentage,
            sales_amount: salesAmount,
            commission_amount: commissionAmount,
            calculated_at: new Date().toISOString()
          })
          .eq('id', existingCommission.id)
          .select()
          .single();

        if (updateError) throw updateError;
        commissions.push(updatedCommission);
      } else {
        // Create new commission
        const { data: newCommission, error: insertError } = await supabase
          .from('booking_commissions')
          .insert({
            business_id: businessId,
            booking_id: bookingId,
            resource_id: resource.id,
            commission_percentage: commissionPercentage,
            sales_amount: salesAmount,
            commission_amount: commissionAmount,
            status: 'pending'
          })
          .select()
          .single();

        if (insertError) throw insertError;
        commissions.push(newCommission);
      }
    } catch (error) {
      console.error(`Error calculating commission for resource ${resource.id}:`, error);
      throw error;
    }
  }

  return commissions;
}

/**
 * Get commission records for a booking
 */
export async function getBookingCommissions(bookingId: string): Promise<BookingCommissionWithResource[]> {
  if (!supabase) throw new Error('Supabase client not initialized');

  const { data, error } = await supabase
    .from('booking_commissions')
    .select(`
      *,
      resource:resources!inner(
        id,
        first_name,
        last_name,
        email
      )
    `)
    .eq('booking_id', bookingId)
    .order('calculated_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

/**
 * Delete commission records for a resource when removed from booking
 */
export async function deleteCommissionForResource(bookingId: string, resourceId: string): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');

  const { error } = await supabase
    .from('booking_commissions')
    .delete()
    .eq('booking_id', bookingId)
    .eq('resource_id', resourceId);

  if (error) throw error;
}

/**
 * Update commission status (e.g., mark as paid)
 */
export async function updateCommissionStatus(
  commissionId: string,
  status: 'pending' | 'paid' | 'voided',
  paidAt?: string
): Promise<BookingCommission> {
  if (!supabase) throw new Error('Supabase client not initialized');

  const updateData: any = { status };
  if (status === 'paid' && paidAt) {
    updateData.paid_at = paidAt;
  }

  const { data, error } = await supabase
    .from('booking_commissions')
    .update(updateData)
    .eq('id', commissionId)
    .select()
    .single();

  if (error) throw error;
  return data;
} 